import { createContext, forwardRef, useContext, type HTMLAttributes, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Minus, type LucideIcon } from 'lucide-react';
import { CURRENCY_SYMBOL_AR, formatMoney, formatPercent, formatQuantity } from '@nova/shared';
import { cn } from '../../lib/utils';
import { Skeleton } from './Skeleton';

/**
 * DS-40 — micro uppercase title, one big monospaced number, an icon badge in a
 * soft coloured square, and a context line underneath. Every dashboard number
 * drills down (RPT-02), so a card with `onClick` announces itself as a target.
 */

export type KpiTone = 'blue' | 'emerald' | 'amber' | 'orange' | 'red' | 'purple' | 'cyan' | 'slate';

export type KpiFormat = 'money' | 'number' | 'percent' | 'text';

/** Written out in full — Tailwind cannot see a class built by interpolation. */
const TONE_BADGE: Record<KpiTone, string> = {
  blue: 'bg-blue-50 text-blue-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  orange: 'bg-orange-50 text-orange-600',
  red: 'bg-red-50 text-red-600',
  purple: 'bg-purple-50 text-purple-600',
  cyan: 'bg-cyan-50 text-cyan-600',
  slate: 'bg-slate-100 text-slate-600',
};

/** KpiRow owns the frame, so a card inside one drops its own border. */
const InRowContext = createContext(false);

export interface KpiCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'onClick'> {
  /** Arabic micro-heading, e.g. «إيراد اليوم». */
  title: string;
  value: string | number;
  /** How a numeric `value` is rendered. Strings are passed through untouched. */
  format?: KpiFormat;
  icon: LucideIcon;
  tone?: KpiTone;
  /** The line under the number — «٢٤ فاتورة» or a comparison period. */
  context?: ReactNode;
  /** Signed percentage change against the comparison period. */
  delta?: number | null;
  /** Which direction is good; cancellations and no-shows are better going down. */
  deltaGood?: 'up' | 'down';
  /** Overrides the unit shown after the number; money defaults to «د.أ». */
  suffix?: ReactNode;
  /** RPT-02 drill-down. Presence alone makes the card interactive. */
  onClick?: () => void;
  loading?: boolean;
}

function renderValue(value: string | number, format: KpiFormat): string {
  if (typeof value === 'string') return value;
  switch (format) {
    case 'money':
      return formatMoney(value);
    case 'percent':
      return formatPercent(value);
    case 'text':
      return String(value);
    default:
      return formatQuantity(value);
  }
}

function Delta({ value, good }: { value: number; good: 'up' | 'down' }) {
  const rising = value > 0;
  const flat = value === 0;
  const positive = good === 'up' ? rising : !rising;
  const Icon = flat ? Minus : rising ? ArrowUp : ArrowDown;

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 font-mono text-[11px] font-bold',
        flat ? 'text-slate-400' : positive ? 'text-emerald-600' : 'text-red-600',
      )}
    >
      <Icon className="h-3 w-3" />
      {/* DS-51 — the percentage stays LTR inside the Arabic context line. */}
      <span className="ltr-num">{formatPercent(Math.abs(value))}</span>
    </span>
  );
}

export const KpiCard = forwardRef<HTMLDivElement, KpiCardProps>(
  (
    {
      title,
      value,
      format = 'number',
      icon: Icon,
      tone = 'blue',
      context,
      delta,
      deltaGood = 'up',
      suffix,
      onClick,
      loading,
      className,
      ...props
    },
    ref,
  ) => {
    const inRow = useContext(InRowContext);
    const clickable = Boolean(onClick);
    const unit = suffix ?? (format === 'money' ? CURRENCY_SYMBOL_AR : undefined);

    const frame = cn(
      'flex flex-col gap-2 p-3',
      !inRow && 'rounded border border-slate-200 bg-white shadow-sm',
      className,
    );

    if (loading) {
      return (
        <div ref={ref} className={frame} {...props}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 space-y-2">
              <Skeleton h={8} w="45%" />
              <Skeleton h={22} w="70%" />
            </div>
            <Skeleton h={32} w={32} className="shrink-0" />
          </div>
          <Skeleton h={9} w="55%" />
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          frame,
          // RPT-02 — a drillable number must look drillable.
          clickable && 'cursor-pointer transition hover:bg-slate-50',
        )}
        onClick={onClick}
        onKeyDown={
          clickable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        {...props}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="label-micro truncate">{title}</p>
            <p className="mt-1 flex items-baseline gap-1 leading-none">
              {/* NFR-05 — DS-40's 24px figure does not fit a two-up card on a
                  375px screen, and a truncated number is a wrong number: an
                  amount reading "1,842.2…" is worse than no card at all. The
                  figure steps down with the card, and the badge steps down with
                  it below `sm` to give the digits the width back. */}
              <span className="ltr-num min-w-0 truncate font-mono text-2xl font-bold text-slate-800 max-lg:text-xl max-sm:text-lg">
                {renderValue(value, format)}
              </span>
              {unit ? <span className="text-[11px] text-slate-400">{unit}</span> : null}
            </p>
          </div>
          {/* DS-40 / DS-45 — soft coloured square, w-5 h-5 icon inside a card. */}
          <span
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded max-sm:h-6 max-sm:w-6',
              TONE_BADGE[tone],
            )}
          >
            <Icon className="h-5 w-5 max-sm:h-4 max-sm:w-4" />
          </span>
        </div>

        {context !== undefined || (delta !== undefined && delta !== null) ? (
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-[11px] text-slate-500">{context}</span>
            {delta !== undefined && delta !== null ? <Delta value={delta} good={deltaGood} /> : null}
          </div>
        ) : null}
      </div>
    );
  },
);
KpiCard.displayName = 'KpiCard';

export interface KpiRowProps extends HTMLAttributes<HTMLDivElement> {
  /** Columns on a wide screen; collapses on narrow ones. Default 4. */
  cols?: 2 | 3 | 4 | 5 | 6;
  /**
   * Breaks the strip into free-standing cards separated by a gap, instead of
   * one panel split by hairlines. The dashboard reads better this way — its
   * cards sit above charts and lists that are already spaced apart.
   */
  separated?: boolean;
}

const ROW_COLS: Record<NonNullable<KpiRowProps['cols']>, string> = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
  6: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
};

/**
 * DS-04 — one surface split by hairlines rather than by gaps.
 *
 * `border-s` on every cell but the first is exactly right while the strip is a
 * single row, and wrong the moment it wraps: the first cell of the second row
 * draws a rule down the outer edge of the panel. NFR-05 wraps it on every phone,
 * so below 1024px the rules come from the grid's own 1px gaps showing the
 * container through instead — which lands correctly for any number of columns,
 * at any number of rows, without counting children.
 */
const ROW_HAIRLINES =
  '[&>*]:border-s [&>*]:border-slate-200 [&>*:first-child]:border-s-0 ' +
  'max-lg:gap-px max-lg:bg-slate-200 max-lg:[&>*]:border-s-0 max-lg:[&>*]:bg-white';

/**
 * DS-04 — the stats strip: one surface, cells split by vertical hairlines
 * rather than by gaps, so a dashboard header reads as a single instrument
 * panel. `border-s` is logical, so the hairline lands between the cells in
 * both directions.
 */
export const KpiRow = forwardRef<HTMLDivElement, KpiRowProps>(
  ({ cols = 4, separated = false, className, children, ...props }, ref) => (
    // Separated cells own their frame again, so each card draws its own border.
    <InRowContext.Provider value={!separated}>
      <div
        ref={ref}
        className={cn(
          'grid',
          separated
            ? 'gap-3'
            : ['overflow-hidden rounded border border-slate-200 bg-white shadow-sm', ROW_HAIRLINES],
          ROW_COLS[cols],
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </InRowContext.Provider>
  ),
);
KpiRow.displayName = 'KpiRow';
